import { compactGBP, formatDate, formatGBP } from "@/lib/utils";
import MiniBarChart from "@/components/MiniBarChart";
import type { Company, CompanyFinancials } from "@/schemas";

export interface ReportViewProps {
  company: Pick<Company, "name" | "country" | "company_type" | "currency"> & {
    [k: string]: unknown;
  };
  financials: CompanyFinancials[];
  /** When rendered as a shared public report, we show a "powered by" footer + generated date. */
  isPublic?: boolean;
  generatedAt?: string | number;
  /** Branded name shown on the footer ("Built with GoCFO"). Optional. */
  brandName?: string;
  /** Where the "Build your own" CTA points (the source site or itsbloxx.com). */
  brandUrl?: string;
}

/**
 * Renders a company's CFO dashboard. Used both in the authenticated app
 * (live data, share button on top) AND on the public /r/:token route
 * (frozen snapshot, no chrome). The component itself stays read-only.
 */
export default function ReportView({
  company,
  financials,
  isPublic,
  generatedAt,
  brandName,
  brandUrl,
}: ReportViewProps) {
  const sortedYears = [...financials].sort((a, b) =>
    a.tax_year.localeCompare(b.tax_year),
  );
  const latest = sortedYears[sortedYears.length - 1];
  const trend = sortedYears.slice(-3);

  return (
    <div className="space-y-10">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            CFO snapshot
          </p>
          <h1 className="mt-1 text-3xl font-semibold">{company.name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {company.country} · {company.company_type} · {company.currency ?? "GBP"}
          </p>
        </div>
        {isPublic && generatedAt ? (
          <p className="text-xs text-muted-foreground">
            Generated {formatDate(generatedAt)}
            {brandName ? (
              <>
                {" · "}
                <a
                  href={brandUrl ?? "https://itsbloxx.com"}
                  className="underline-offset-4 hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  {brandName}
                </a>
              </>
            ) : null}
          </p>
        ) : null}
      </header>

      {latest ? (
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <HeroStat label="Revenue" value={formatGBP(latest.revenue)} hint={latest.tax_year} />
          <HeroStat
            label="Net profit"
            value={formatGBP(latest.net_profit)}
            hint={latest.tax_year}
          />
          <HeroStat
            label="Corp tax"
            value={formatGBP(latest.corporation_tax)}
            hint="Latest year"
          />
          <HeroStat
            label="Cash in bank"
            value={formatGBP(latest.cash_in_bank)}
            hint="Latest balance"
          />
        </section>
      ) : (
        <section className="rounded-lg border p-8 text-center text-muted-foreground">
          No financials yet. Connect FreeAgent to pull data.
        </section>
      )}

      {trend.length > 0 ? (
        <section className="rounded-lg border p-6">
          <h2 className="text-lg font-semibold">Revenue &amp; net profit</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Last {trend.length} year{trend.length === 1 ? "" : "s"}
          </p>
          <div className="mt-4">
            <MiniBarChart
              categories={trend.map((y) => y.tax_year)}
              series={[
                {
                  label: "Revenue",
                  fillClass: "fill-foreground",
                  values: trend.map((y) => y.revenue),
                },
                {
                  label: "Net profit",
                  fillClass: "fill-primary",
                  values: trend.map((y) => y.net_profit),
                },
              ]}
              formatValue={(v) => compactGBP(v)}
            />
          </div>
        </section>
      ) : null}

      {trend.length > 0 ? (
        <section className="rounded-lg border p-6">
          <h2 className="text-lg font-semibold">By tax year</h2>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-2 font-normal">Year</th>
                  <th className="py-2 text-right font-normal">Revenue</th>
                  <th className="py-2 text-right font-normal">Net profit</th>
                  <th className="py-2 text-right font-normal">Dividends</th>
                  <th className="py-2 text-right font-normal">Cash</th>
                </tr>
              </thead>
              <tbody>
                {[...sortedYears].reverse().map((y) => (
                  <tr key={y.id} className="border-t">
                    <td className="py-2 font-medium">{y.tax_year}</td>
                    <td className="py-2 text-right">{formatGBP(y.revenue)}</td>
                    <td className="py-2 text-right">{formatGBP(y.net_profit)}</td>
                    <td className="py-2 text-right">{formatGBP(y.total_dividends_paid)}</td>
                    <td className="py-2 text-right">{formatGBP(y.cash_in_bank)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {isPublic ? (
        <footer className="border-t pt-6 text-center">
          <p className="text-sm text-muted-foreground">
            This is a read-only snapshot generated by{" "}
            <a
              href={brandUrl ?? "https://itsbloxx.com"}
              className="font-medium text-foreground underline-offset-4 hover:underline"
            >
              {brandName ?? "Bloxx"}
            </a>{" "}
            — connect your own FreeAgent to build one in 60 seconds.
          </p>
        </footer>
      ) : null}
    </div>
  );
}

function HeroStat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border p-5">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-2 text-2xl font-semibold">{value}</div>
      {hint ? <div className="mt-1 text-xs text-muted-foreground">{hint}</div> : null}
    </div>
  );
}
